import { Info } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { inr, type UpgradeProration } from '@/lib/sales-metrics';

const fmtDate = (iso: string): string => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

interface RowProps {
  label: string;
  value: string;
  strong?: boolean;
}

const Row = ({ label, value, strong = false }: RowProps) => (
  <div
    className={`flex items-baseline justify-between gap-3 sm:gap-4 ${strong ? 'font-semibold text-foreground' : ''}`}
  >
    <span className={`min-w-0 ${strong ? '' : 'text-muted-foreground'}`}>{label}</span>
    <span className="shrink-0 whitespace-nowrap tabular-nums">{value}</span>
  </div>
);

interface UpgradeProrationInfoProps {
  proration: UpgradeProration;
  /** Display names for the two plan codes, when known. */
  fromName?: string;
  toName?: string;
}

/**
 * The working behind an upgrade's price, on demand.
 *
 * The quotation shows a single figure that is neither plan's list price, so
 * without this the RM has no way to explain it to the client. Every number here
 * comes from the server's own computation - nothing is re-derived, so this can
 * never disagree with what is actually charged.
 */
export const UpgradeProrationInfo = ({
  proration,
  fromName,
  toName,
}: UpgradeProrationInfoProps) => {
  const {
    basis,
    prorated,
    fromPrice,
    toPrice,
    difference,
    monthsRemaining,
    periodMonths,
    amount,
    aumCrores,
    inheritedDueDate,
  } = proration;

  const basisLabel = basis === 'setup' ? 'setup price' : 'renewal price';
  const periodLabel = basis === 'setup' ? 'first year of the plan' : 'current renewal cycle';
  const from = fromName ?? proration.fromProductCode;
  const to = toName ?? proration.toProductCode;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="How this upgrade price was calculated"
          className="-my-2 ml-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full align-middle text-muted-foreground transition hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(20rem,calc(100vw-2rem))] text-xs">
        <p className="text-sm font-bold text-foreground">Upgrade pricing</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          {prorated
            ? `Only the difference between the two plans is charged, for the months left in the ${periodLabel}.`
            : `${from} was never activated, so none of it has been used. The full difference between the two plans is charged.`}
        </p>

        <div className="mt-3 space-y-1.5 border-t border-border pt-2.5">
          <Row label={`${to} ${basisLabel}`} value={inr(toPrice)} />
          <Row label={`${from} ${basisLabel}`} value={`− ${inr(fromPrice)}`} />
          <div className="border-t border-border pt-1.5">
            <Row label="Difference" value={inr(difference)} strong />
          </div>
        </div>

        <div className="mt-3 space-y-1.5 border-t border-border pt-2.5">
          {prorated && (
            <Row label="Months remaining" value={`${monthsRemaining} of ${periodMonths}`} />
          )}
          <div className={prorated ? 'border-t border-border pt-1.5' : ''}>
            <Row label="Payable now" value={inr(amount)} strong />
          </div>
          {prorated && (
            <p className="pt-0.5 text-[10px] text-muted-foreground tabular-nums">
              {`${inr(difference)} × ${monthsRemaining}/${periodMonths} = ${inr(amount)}`}
            </p>
          )}
        </div>

        <p className="mt-3 border-t border-border pt-2.5 text-[10px] text-muted-foreground">
          {`Both plans priced at the client's combined AUM of ${aumCrores} Cr. `}
          {inheritedDueDate
            ? `The renewal date does not change - they renew on ${fmtDate(inheritedDueDate)} at ${to}'s price.`
            : `${to}'s own term starts when the Service team activates it.`}
        </p>
        {prorated && monthsRemaining === 0 && (
          <p className="mt-2 rounded-md bg-muted/50 p-2 text-[10px] text-muted-foreground">
            Less than a whole month is left before renewal, so there is nothing to prorate - the
            client is billed the higher plan in full at renewal.
          </p>
        )}
        {!prorated && (
          <p className="mt-2 rounded-md bg-muted/50 p-2 text-[10px] text-muted-foreground">
            {`Once this upgrade is handed over to billing, ${from} is withdrawn from the service queue so it is never provisioned.`}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};
